import * as React from 'react';
import { jsx, css, keyframes } from '@emotion/react';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { FeaturePropsType } from '../../../../../chat/views/types';
import type { RenderSimpleButtonGroupPropsType } from '../../../../../chat/views/SimpleButtonGroup/index';
import { scrollToBottomOfChat } from '../../../../../chat/functional/scrollToBottomOfChat';
import { SimpleButton } from './components/SimpleButton';

const { tablet, mobile } = mediaQueries();

export type SimpleButtonGroupPropsType = FeaturePropsType<RenderSimpleButtonGroupPropsType>

export const SimpleButtonGroup = (props: SimpleButtonGroupPropsType) => {
  const {
    keyName,
    isInstant,
    doesNotScroll,
    enableHideAnimation: enableHideAnimationFromProps = true,
    shouldEnableHideAnimation,
    balloonContents,
    continueHandler,
    answers,
  } = props;
  const [isAnswered, setIsAnswered] = React.useState(false);
  const [showAnswerPanel, setShowAnswerPanel] = React.useState(true);
  const [isShow, setIsShow] = React.useState(isInstant);
  const [enableHideAnimation, setEnableHideAnimation] = React.useState(enableHideAnimationFromProps);
  const answerButtonHasBalloon = !!(answers.find((answer) => answer?.balloonContents));

  React.useEffect(() => {
    (async () => {
      if (!isInstant) {
        await wait(0.4);

        setIsShow(true);
      }
    })();
  }, [isInstant]);

  React.useEffect(() => {
    if (!doesNotScroll) scrollToBottomOfChat();
  }, [doesNotScroll]);

  return (
    <div
      css={css`
        display: grid;
        grid-template-rows: minmax(0, 1fr);
        transition: grid-template-rows ${setTime(0.4)}s;

        ${!showAnswerPanel && css`
          overflow: hidden;
          grid-template-rows: minmax(0, 0fr);

          ${!enableHideAnimation && css`
            display: none;
          `}
        `}
      `}
    >
      <div
        css={css`
          padding: 30px 12px;
          position: relative;
          opacity: 0;
          bottom: -10px;
          transition: opacity ${setTime(0.4)}s ${setTime(0.4)}s, bottom ${setTime(0.4)}s ${setTime(0.4)}s;

          ${balloonContents && css`
            padding-bottom: 50px;
          `}

          ${answerButtonHasBalloon && css`
            padding-top: 50px;
          `}

          ${isShow && css`
            opacity: 1;
            bottom: 0;
          `}
        `}
      >
        <div
          css={css`
            max-width: 476px;
            margin-inline: auto;
            padding: 20px 18px;
            background-color: #bae8e8;
            position: relative;

            ${(keyName === 'region' || keyName === 'prefecture' || keyName === 'ageOfBuilding') && css`
              max-width: 626px;
            `}

            ${mobile(({ clamp }) => css`
              padding: ${clamp('8px', '12px')};
              border-radius: 4px;
            `)}
          `}
        >
          <div
            css={css`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px 16px;

              ${mobile(() => css`
                gap: 10px;
              `)}

              ${answers.length === 3 && css`
                grid-template-columns: 1fr 1fr 1fr;

                ${mobile(({ clamp }) => css`
                  gap: 10px ${clamp('6px', '10px')};
                `)}
              `}

              ${answers.length >= 7 && css`
                grid-template-columns: 1fr 1fr 1fr 1fr;

                ${mobile(() => css`
                  grid-template-columns: 1fr 1fr;
                `)}
              `}

              ${keyName === 'prefecture' && css`
                ${answers.length === 4 && css`
                  grid-template-columns: 1fr 1fr 1fr 1fr;

                  ${mobile(() => css`
                    grid-template-columns: 1fr 1fr;
                  `)}
                `}

                ${(answers.length === 5 || answers.length === 6) && css`
                  grid-template-columns: 1fr 1fr 1fr;

                  ${mobile(() => css`
                    grid-template-columns: 1fr 1fr;
                  `)}
                `}
              `}

              ${answerButtonHasBalloon && css`
                gap: 30px 16px;

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  gap: ${setAutomaticDesignBasedSize(30, 30)} 10px;
                `)}
              `}
            `}
          >
            {answers.map((answer) => {
              const { id, answerHandler } = answer;
              const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
                const { value, textContent } = event.currentTarget;

                // 回答済みフラグを立てる
                setIsAnswered(true);

                // 個別の回答処理を実行
                answerHandler(event);

                // 回答によって非表示のアニメーションを無効にする処理
                if (shouldEnableHideAnimation) {
                  setEnableHideAnimation(shouldEnableHideAnimation(textContent));
                }

                await wait(0.5);

                // 回答パネルを非表示にする
                setShowAnswerPanel(false);

                if (enableHideAnimation) await wait(0.5);

                // 回答内容を渡して次の処理に進む
                continueHandler({
                  value,
                  answerText: textContent,
                  selfDomReferences: [],
                });
              };

              const { balloonContents, ...buttonProps } = props;

              return (
                <SimpleButton
                  key={id}
                  {...buttonProps}
                  {...answer}
                  isAnswered={isAnswered}
                  onClickHandler={onClickHandler}
                />
              );
            })}
          </div>
          {balloonContents && (
            <div
              css={css`
                color: #136f84;
                font-size: 14px;
                text-align: center;
                white-space: nowrap;
                min-width: 50px;
                padding: 6px 12px;
                background-color: #aef1ff;
                border-radius: 6px;
                filter: drop-shadow(0 1px 4px rgb(0 0 0 / 0.6));
                position: absolute;
                top: calc(100% - 6px);
                left: 50%;
                transform: translateX(-50%);
                opacity: 0;
                visibility: hidden;
                animation: ${setTime(0.4)}s ${setTime(3)}s forwards ${keyframes`
                  from {
                    opacity: 0;
                    visibility: hidden;
                    top: calc(100% - 6px) ;
                  }

                  to {
                    opacity: 1;
                    visibility: visible;
                    top: calc(100% + 4px) ;
                  }
                `};
                pointer-events: none;

                ${tablet(() => css`
                  transform: translate3d(-50%, 0, 0);
                `)}

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(14, 14)};
                  min-width: ${setAutomaticDesignBasedSize(50, 50)};
                  padding: ${setAutomaticDesignBasedSize(6, 6)} ${setAutomaticDesignBasedSize(12, 12)};
                `)}

                &::before {
                  content: "";
                  width: 16px;
                  height: 10px;
                  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
                  background-color: #aef1ff;
                  position: absolute;
                  bottom: calc(100% - 1px);
                  left: 50%;
                  transform: translateX(-50%);

                  ${mobile(({ setAutomaticDesignBasedSize }) => css`
                    width: ${setAutomaticDesignBasedSize(16, 16)};
                    height: ${setAutomaticDesignBasedSize(10, 10)};
                  `)}
                }
              `}
            >
              {balloonContents}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
